import { AllFilters } from './../../../../../shared/models/all-filter.model';
import { inject } from '@angular/core';
import {
  AnimalsService,
  FilesService,
  PaginationResult,
  UploadedFileDto,
} from '@anihis/core/data-access';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { DateTime } from 'luxon';
import { finalize, map, tap } from 'rxjs/operators';
import { LoadingStatus } from '../../../../../shared/enums/loading';
import { initialFilters } from 'apps/anihis-portal/src/app/shared/models/all-initial-filters';

export type DocumentsQuery = {
  pageNumber: number;
  pageSize: number;
  filters: AllFilters;
  pagination: PaginationResult | undefined;
};

type DocumentState = {
  data: UploadedFileDto[] | undefined;
  animalUid: string | undefined;
  status: LoadingStatus | undefined;
  statusSearch: LoadingStatus | undefined;
  pagination: PaginationResult | undefined;
  query: DocumentsQuery;
};

const emptyQuery: DocumentsQuery = {
  pageNumber: 1,
  pageSize: 15,
  filters: initialFilters,
  pagination: undefined,
};

const initialState: DocumentState = {
  data: undefined,
  animalUid: undefined,
  status: undefined,
  statusSearch: undefined,
  pagination: undefined,
  query: emptyQuery,
};

export const DocumentStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      animalsService = inject(AnimalsService),
      filesService = inject(FilesService),
    ) => {
      return {
        setAnimalUid(animalUid: string) {
          patchState(store, { animalUid: animalUid });
        },

        toStartOfDay(date: Date | null | undefined): Date | undefined {
          return date
            ? DateTime.fromJSDate(date).startOf('day').toJSDate()
            : undefined;
        },

        toEndOfDay(date: Date | null | undefined): Date | undefined {
          return date
            ? DateTime.fromJSDate(date).endOf('day').toJSDate()
            : undefined;
        },

        fetchDocuments() {
          const { filters, pageSize } = store.query();
          patchState(store, { status: LoadingStatus.Loading });
          return animalsService
            .animalsFilesGet(
              store.animalUid(),
              filters.search,
              this.toStartOfDay(filters.range?.start),
              this.toEndOfDay(filters.range?.end),
              1,
              pageSize,
            )
            .pipe(
              tap((res) => {
                patchState(store, {
                  data: res.files,
                  pagination: {
                    pageNumber: res.pageNumber,
                    pageSize: res.pageSize,
                    totalCount: res.totalCount,
                    totalPages: res.totalPages,
                    hasNextPage: res.hasNextPage,
                    hasPreviousPage: res.hasPreviousPage,
                  },
                });
              }),

              finalize(() =>
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                }),
              ),
            );
        },

        updateDateRangeFilter(
          start: Date | null | undefined,
          end: Date | null | undefined,
        ) {
          patchState(store, {
            query: {
              ...store.query(),
              pageNumber: 1,
              filters: {
                ...store.query().filters,
                range: start && end ? { start, end } : undefined,
              },
            },
          });
          this.fetchDocuments().subscribe();
        },

        updateSearch(search: string) {
          patchState(store, {
            statusSearch: LoadingStatus.Loading,
            query: {
              ...store.query(),
              pageNumber: 1,
              filters: {
                ...store.query().filters,
                search,
              },
            },
          });
          this.fetchDocuments().subscribe();
        },

        appendData(
          newHealthRecords: UploadedFileDto[],
          pagination: PaginationResult,
        ) {
          const current = store.data() ?? [];
          const existingUids = new Set(current.map((x) => x.uid));

          const uniqueNew = newHealthRecords.filter(
            (x) => !existingUids.has(x.uid),
          );

          patchState(store, {
            data: [...current, ...uniqueNew],
            status: LoadingStatus.Loaded,
            pagination: {
              pageNumber: pagination.pageNumber,
              pageSize: pagination.pageSize,
              totalCount: pagination.totalCount,
              totalPages: pagination.totalPages,
              hasNextPage: pagination.hasNextPage,
              hasPreviousPage: pagination.hasPreviousPage,
            },
          });
        },

        loadMoreData() {
          const { filters, pageSize } = store.query();
          const nextPage = store.pagination()?.pageNumber! + 1;
          patchState(store, { status: LoadingStatus.Loading });
          return animalsService
            .animalsFilesGet(
              store.animalUid(),
              undefined,
              this.toStartOfDay(filters.range?.start),
              this.toEndOfDay(filters.range?.end),
              nextPage,
              pageSize,
            )
            .pipe(
              tap((res) => {
                this.appendData(res.files ?? [], {
                  pageNumber: res.pageNumber ?? nextPage,
                  pageSize: res.pageSize ?? pageSize,
                  totalCount: res.totalCount ?? 0,
                  totalPages: res.totalPages ?? nextPage,
                  hasNextPage: res.hasNextPage ?? false,
                  hasPreviousPage: res.hasPreviousPage ?? false,
                });
              }),
              finalize(() =>
                patchState(store, {
                  status: LoadingStatus.Loaded,
                  statusSearch: LoadingStatus.Loaded,
                }),
              ),
            );
        },
        getDocument(documentUid: string) {
          return filesService
            .filesUidGet(documentUid)
            .pipe(map((result) => result.file?.url));
        },
      };
    },
  ),
);
